
import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Loading from '@components/Shared/Loader/Loading';
import { Card, CardFooter, CardHeader } from '@components/card/card';

import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initReport, getReportPagedData, selectReport, setReportRequest } from '@store/DynamicSlice/reportTableSlice';

import ReportExportExcelModal, { type ExportColumn } from '@Helper/ReportPaged/ReportExportExcelModal';
import ReportPagination from '@Helper/ReportPaged/ReportPagination';

import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';

/** =========================
 * REQUEST SHAPE
 * ========================= */
type ApInvoicesDetailsReq = {
  pageIndex?: number;
  pageSize?: number;
  froM_DATE?: string | null;
  tO_DATE?: string | null;
  brancH_ID?: string | null;
  invoicE_TYPE?: string | null;
  referencE_TYPE?: string | null;
  supplieR_ID?: string | null;
};

/** =========================
 * RESPONSE SHAPE (Array items)
 * ========================= */
type RowDto = {
  invoicE_ID: string | null;
  code: string | null;
  invoicE_DATE: string | null;
  invoicE_TYPE: string | null;
  referencE_TYPE: string | null;
  supP_NAME: string | null;
  supP_VAT_CODE: string | null;
  brancH_NAME: string | null;
  brancH_NAME2: string | null;
  vaT_AMOUNT: number;
  total: number;
  b_VAT: number;
};

const REPORT_KEY = 'RPT_ApInvoicesDetails';
const ENDPOINT = 'FinReports/GetApInvoicesDetails';

const safe = (v: any, fallback = '—') => (v === null || v === undefined || v === '' ? fallback : String(v));

const formatNumber = (num: number | null | undefined, digits: number = 2) => {
  if (num === null || num === undefined) return '0.00';
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(
    num || 0
  );
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
};

const isoOrNull = (d: any) => {
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00.000Z`;
  if (d instanceof Date) return d.toISOString();
  return null;
};

const cleanNulls = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
};

const getOptId = (opt: any): string | null => {
  const v = opt?.value ?? opt?.id ?? opt?.ID ?? opt?.key ?? null;
  return v ? String(v) : null;
};

const metaNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const ApInvoicesDetails_Paged: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language !== 'en';

  const dispatch = useAppDispatch();

  // ===== Options
  const branchHook = useGetOperatingUnitWithPermission() as any;
  const OptionsBranches = branchHook?.OperatingUnitOptions ?? branchHook?.OptionsBranches ?? [];
  const loadingBranches = branchHook?.loading ?? false;

  const suppHook = useGetSuppliersList() as any;
  const OptionsSuppliers = suppHook?.OptionSuppliers ?? suppHook?.OptionsSuppliers ?? suppHook?.options ?? [];
  const loadingSuppliers = suppHook?.loading ?? false;

  // Reference Types: Bill | EXP | AP_INVOICE | Discount
  const optionsReferenceType = useMemo(
    () => [
      { value: 'Bill', label: isAr ? 'فاتورة مشتريات' : 'Bill' },
      // { value: 'EXP', label: isAr ? 'مصروفات' : 'Expenses (EXP)' },
      // { value: 'EXP_FAST', label: isAr ? 'مصروفات' : 'Expenses (EXP)' },
      { value: 'EXP', label: isAr ? 'مصروفات' : 'Expenses (EXP)' },
      { value: 'AP_INVOICE', label: isAr ? 'فاتورة مدفوعات' : 'AP Invoice' },
      { value: 'Discount', label: isAr ? 'خصم' : 'Discount' }
    ],
    [isAr]
  );

  // Invoice Types: Standard | CreditMemo
  const optionsInvoiceType = useMemo(
    () => [
      { value: 'Standard', label: isAr ? 'قياسي (فاتورة)' : 'Standard' },
      { value: 'CreditMemo', label: isAr ? 'إشعار مدين (مردود)' : 'Debit Memo' }
    ],
    [isAr]
  );

  // ===== Filters
  const [branchFilter, setBranchFilter] = useState<any>(null);
  const [supplierFilter, setSupplierFilter] = useState<any>(null);
  const [referenceTypeFilter, setReferenceTypeFilter] = useState<any>(null);
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<any>(null);
  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);

  const [showExport, setShowExport] = useState(false);

  // ===== Report state
  const report = useAppSelector(s => selectReport(s as any, REPORT_KEY));
  const items = (report?.items ?? []) as RowDto[];
  const meta = report?.meta ?? null;
  const loading = report?.loading ?? false;
  const error = report?.error ?? null;

  useEffect(() => {
    const initialRequest: ApInvoicesDetailsReq = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(
      initReport({
        reportKey: REPORT_KEY,
        endpoint: ENDPOINT,
        initialRequest
      })
    );
  }, [dispatch]);

  const applyFilters = () => {
    const prevReq = (((report as any)?.request ?? {}) as ApInvoicesDetailsReq) || {};

    const nextReq: ApInvoicesDetailsReq = {
      ...prevReq,
      pageIndex: 0,
      pageSize: prevReq.pageSize ?? 50,
      froM_DATE: isoOrNull(fromDate),
      tO_DATE: isoOrNull(toDate),
      brancH_ID: getOptId(branchFilter),
      supplieR_ID: getOptId(supplierFilter),
      referencE_TYPE: getOptId(referenceTypeFilter),
      invoicE_TYPE: getOptId(invoiceTypeFilter)
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: cleanNulls(nextReq) as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  const clearFilters = () => {
    setBranchFilter(null);
    setSupplierFilter(null);
    setReferenceTypeFilter(null);
    setInvoiceTypeFilter(null);
    setFromDate(null);
    setToDate(null);

    const resetReq: ApInvoicesDetailsReq = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: resetReq as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  // Helper for doc routing
  const getDocLink = (id: string | null) => {
    if (!id) return null;
    // مسار افتراضي لفتح الفاتورة (عدّله حسب الراوت المعتمد لديك)
    return `/AP/ApInvoices/${id}/UpdateApInvoices`;
  };

  const TotalsCards = () => {
    if (!meta) return null;

    const totalBeforeVat = metaNum((meta as any).total_BEFORE_VAT);
    const totalVat = metaNum((meta as any).total_VAT);
    const totalNetSale = metaNum((meta as any).total_NET_SALE);

    const hasAny = totalBeforeVat !== 0 || totalVat !== 0 || totalNetSale !== 0;
    if (!hasAny && items.length === 0) return null;

    return (
      <div className='row g-3 mb-4'>
        {/* إجمالي قبل الضريبة */}
        <div className='col-lg-4 col-md-6'>
          <Card className='bg-primary bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-file-invoice-dollar text-primary fa-xl'></i>
              </div>
              <div>
                <h6 className='text-primary fw-bold mb-1'>{isAr ? 'الإجمالي قبل الضريبة' : 'Total Before VAT'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(totalBeforeVat)}</h4>
              </div>
            </div>
          </Card>
        </div>

        {/* إجمالي الضريبة */}
        <div className='col-lg-4 col-md-6'>
          <Card className='bg-warning bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-percent text-warning fa-xl'></i>
              </div>
              <div>
                <h6 className='text-warning fw-bold mb-1'>{isAr ? 'إجمالي الضريبة' : 'Total VAT'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(totalVat)}</h4>
              </div>
            </div>
          </Card>
        </div>

        {/* إجمالي الصافي */}
        <div className='col-lg-4 col-md-6'>
          <Card className='bg-success bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-sack-dollar text-success fa-xl'></i>
              </div>
              <div>
                <h6 className='text-success fw-bold mb-1'>{isAr ? 'الصافي' : 'Total Net'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(totalNetSale)}</h4>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // إجماليات الصفحة الحالية (Footer)
  const totalsFooter = useMemo(() => {
    return items.reduce(
      (acc, r) => {
        acc.totalBeforeVat += r.b_VAT || 0;
        acc.totalVat += r.vaT_AMOUNT || 0;
        acc.totalNet += r.total || 0;
        return acc;
      },
      { totalBeforeVat: 0, totalVat: 0, totalNet: 0 }
    );
  }, [items]);

  const exportColumns: ExportColumn<RowDto>[] = [
    { headerAr: 'التاريخ', headerEn: 'Date', value: r => formatDate(r.invoicE_DATE) },
    { headerAr: 'رقم الفاتورة', headerEn: 'Invoice No', value: r => r.code || '' },
    { headerAr: 'الفرع', headerEn: 'Branch', value: r => (isAr ? r.brancH_NAME2 || r.brancH_NAME : r.brancH_NAME || r.brancH_NAME2) || '' },
    { headerAr: 'المورد', headerEn: 'Supplier', value: r => r.supP_NAME || '' },
    { headerAr: 'الرقم الضريبي', headerEn: 'VAT Code', value: r => r.supP_VAT_CODE || '' },
    { headerAr: 'المرجع', headerEn: 'Ref Type', value: r => r.referencE_TYPE || '' },
    { headerAr: 'نوع الفاتورة', headerEn: 'Invoice Type', value: r => r.invoicE_TYPE || '' },
    { headerAr: 'قبل الضريبة', headerEn: 'Before VAT', value: r => r.b_VAT || 0 },
    { headerAr: 'الضريبة', headerEn: 'VAT Amount', value: r => r.vaT_AMOUNT || 0 },
    { headerAr: 'الإجمالي', headerEn: 'Total', value: r => r.total || 0 },
  ];

  return (
    <>
      <CardHeader className='mb-3'>
        <div className='row g-2 align-items-center mb-2'>
          {/* Branch */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold' title={isAr ? 'الفرع' : 'Branch'}>
                <i className='fas fa-store text-primary'></i>
              </span>
              <Select
                isLoading={loadingBranches}
                value={branchFilter}
                onChange={setBranchFilter}
                options={OptionsBranches}
                placeholder={isAr ? 'الفرع' : 'Branch'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* Supplier */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold' title={isAr ? 'المورد' : 'Supplier'}>
                <i className='fas fa-truck text-danger'></i>
              </span>
              <Select
                isLoading={loadingSuppliers}
                value={supplierFilter}
                onChange={setSupplierFilter}
                options={OptionsSuppliers}
                placeholder={isAr ? 'المورد' : 'Supplier'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* Reference Type */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold' title={isAr ? 'نوع المرجع' : 'Reference Type'}>
                <i className='fas fa-link text-info'></i>
              </span>
              <Select
                value={referenceTypeFilter}
                onChange={setReferenceTypeFilter}
                options={optionsReferenceType}
                placeholder={isAr ? 'المرجع' : 'Reference Type'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className='col-lg-3 text-end'>
            <button
              className='btn btn-outline-secondary me-2'
              type='button'
              onClick={clearFilters}
              title={t('ClearFilters')}
            >
              <i className='fas fa-times fa-fw'></i>
            </button>
            <button className='btn btn-theme me-2' type='button' onClick={applyFilters} title={t('ApplyFilters')}>
              <i className='fas fa-filter fa-fw'></i>
            </button>
            <button
              className='btn btn-outline-success'
              type='button'
              onClick={() => setShowExport(true)}
              title={t('Export')}
              disabled={loading || items.length === 0}
            >
              <i className='fas fa-file-excel fa-fw'></i>
            </button>
          </div>
        </div>

        <div className='row g-2 align-items-center'>
          {/* Invoice Type */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold' title={isAr ? 'نوع الفاتورة' : 'Invoice Type'}>
                <i className='fas fa-file-alt text-secondary'></i>
              </span>
              <Select
                value={invoiceTypeFilter}
                onChange={setInvoiceTypeFilter}
                options={optionsInvoiceType}
                placeholder={isAr ? 'نوع الفاتورة' : 'Invoice Type'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* From Date */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={fromDate || ''}
                onChange={e => setFromDate(e.target.value)}
                placeholder={isAr ? 'من تاريخ' : 'From Date'}
              />
            </div>
          </div>

          {/* To Date */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={toDate || ''}
                onChange={e => setToDate(e.target.value)}
                min={fromDate || undefined}
                placeholder={isAr ? 'إلى تاريخ' : 'To Date'}
              />
            </div>
          </div>
        </div>
      </CardHeader>

      <TotalsCards />

      <Card className='mb-4'>
        <Loading loading={loading} error={error}>
          <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table className='table table-hover table-bordered mb-0'>
              <thead className='sticky-top align-middle' style={{ top: 0, zIndex: 10 }}>
                <tr className='bg-theme bg-opacity-35 text-center' style={{ backdropFilter: 'blur(20px)' }}>
                  <th className='small text-primary' colSpan={7}>
                    {isAr ? 'بيانات الفاتورة' : 'Invoice Info'}
                  </th>
                  <th className='small text-success' colSpan={3}>
                    {isAr ? 'المبالغ' : 'Amounts'}
                  </th>
                </tr>

                <tr className='bg-theme bg-opacity-25 text-center' style={{ backdropFilter: 'blur(20px)' }}>
                  <th className='small py-2' style={{ minWidth: 110 }}>{t('Date')}</th>
                  <th className='small py-2' style={{ minWidth: 140 }}>{isAr ? 'رقم الفاتورة' : 'Invoice No'}</th>
                  <th className='small py-2' style={{ minWidth: 160 }}>{isAr ? 'الفرع' : 'Branch'}</th>
                  <th className='small py-2' style={{ minWidth: 220 }}>{isAr ? 'المورد' : 'Supplier'}</th>
                  <th className='small py-2' style={{ minWidth: 150 }}>{isAr ? 'الرقم الضريبي' : 'VAT Code'}</th>
                  <th className='small py-2' style={{ minWidth: 120 }}>{isAr ? 'المرجع' : 'Ref Type'}</th>
                  <th className='small py-2' style={{ minWidth: 120 }}>{isAr ? 'نوع الفاتورة' : 'Invoice Type'}</th>

                  <th className='small text-end py-2' style={{ minWidth: 120 }}>{isAr ? 'قبل الضريبة' : 'Before VAT'}</th>
                  <th className='small text-end py-2' style={{ minWidth: 120 }}>{isAr ? 'الضريبة' : 'VAT'}</th>
                  <th className='small text-end py-2' style={{ minWidth: 120 }}>{isAr ? 'الإجمالي' : 'Total'}</th>
                </tr>
              </thead>

              <tbody>
                {!loading && items.length > 0 ? (
                  items.map((r, idx) => {
                    const branchName = isAr ? r.brancH_NAME2 || r.brancH_NAME : r.brancH_NAME || r.brancH_NAME2;
                    const docLink = getDocLink(r.invoicE_ID);

                    return (
                      <tr key={`${r.invoicE_ID || idx}`} className='align-middle'>
                        <td className='text-center'>{formatDate(r.invoicE_DATE)}</td>
                        <td className='text-center'>
                          {docLink ? (
                            <Link to={docLink} className='fw-bold text-primary text-decoration-none' target='_blank'>
                              {safe(r.code)}
                            </Link>
                          ) : (
                            <span className='fw-bold text-primary'>{safe(r.code)}</span>
                          )}
                        </td>
                        <td className='text-start'>{safe(branchName)}</td>
                        <td className='text-start fw-bold'>{safe(r.supP_NAME)}</td>
                        <td className='text-center'>{safe(r.supP_VAT_CODE)}</td>
                        <td className='text-center'>
                          <span className='badge bg-info bg-opacity-10 text-info border border-info'>
                            {safe(r.referencE_TYPE)}
                          </span>
                        </td>
                        <td className='text-center'>
                          <span className={`badge ${r.invoicE_TYPE === 'CreditMemo' ? 'bg-danger text-danger border-danger' : 'bg-secondary text-secondary border-secondary'} bg-opacity-10 border`}>
                            {safe(r.invoicE_TYPE) === 'CreditMemo' ? "إشعار مدين" :"قيـاسي"}
                          </span>
                        </td>

                        <td className='text-end fw-bold'>{formatNumber(r.b_VAT)}</td>
                        <td className='text-end fw-bold text-warning'>{formatNumber(r.vaT_AMOUNT)}</td>
                        <td className='text-end fw-bold text-success'>{formatNumber(r.total)}</td>
                      </tr>
                    );
                  })
                ) : !loading ? (
                  <tr>
                    <td colSpan={10} className='text-center py-5'>
                      <div className='text-muted'>
                        <i className='fas fa-file-invoice fa-3x mb-3 opacity-50'></i>
                        <h6>{t('NoData')}</h6>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>

              {/* إجماليات الصفحة */}
              {!loading && items.length > 0 && (
                <tfoot className='fw-bold bg-secondary bg-opacity-30'>
                  <tr>
                    <td colSpan={7} className='text-end'>
                      <span className='h6 m-0'>{isAr ? 'الإجمالي (الصفحة):' : 'Totals (page):'}</span>
                    </td>
                    <td className='text-end'>
                      <span className='h6 m-0'>{formatNumber(totalsFooter.totalBeforeVat)}</span>
                    </td>
                    <td className='text-end text-warning'>
                      <span className='h6 m-0'>{formatNumber(totalsFooter.totalVat)}</span>
                    </td>
                    <td className='text-end text-success'>
                      <span className='h6 m-0'>{formatNumber(totalsFooter.totalNet)}</span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Loading>

        <CardFooter>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
            <ReportPagination reportKey={REPORT_KEY} maxVisiblePages={3} showItemsPerPage={true} />
            {!loading && items.length > 0 && (
              <div className='text-muted small'>
                <i className='fas fa-info-circle me-1'></i>
                {isAr ? 'عرض' : 'Showing'} {items.length} {isAr ? 'من' : 'of'} {meta?.totalItems || 0}{' '}
                {isAr ? 'سجل' : 'records'}
              </div>
            )}
          </div>
        </CardFooter>
      </Card>

      <ReportExportExcelModal
        show={showExport}
        onHide={() => setShowExport(false)}
        reportKey={REPORT_KEY}
        endpoint={ENDPOINT}
        columns={exportColumns}
        exportPageSize={4000}
        sheetName={({ isAr }) => (isAr ? 'تفاصيل فواتير المدفوعات' : 'AP Invoices Details')}
        fileName={({ reportKey }) => `${reportKey}_${new Date().toISOString().slice(0, 10)}.xlsx`}
      />
    </>
  );
};

export default ApInvoicesDetails_Paged;